import React from "react";

import {ExternalLink} from "../../component/IconLink";
import ImgGallery from "../../component/ImgGallery";
import Link from "../../component/Link";
import {SpecificTag} from "../../views/Tag";

export default function PageContent() {
    return (<>
        <section>
            <h2>Building HaDiKo a Website It Deserves</h2>
            <p>
                During my studies I lived in the <ExternalLink href="https://hadiko.de">Hans-Dickmann-Kolleg (HaDiKo)</ExternalLink>, Germany's largest self-administered student dormitory &mdash; over a thousand rooms across six houses, run entirely by the students who live in them.
                No landlord, no management company, just committees, elected boards and a lot of unpaid volunteer work keeping the place running.
                Naturally, the website that represents all of this deserved better than what it had.
            </p>
            <p>
                I took on designing and building the new <ExternalLink href="https://hadiko.de">hadiko.de</ExternalLink> from the ground up: the visual identity, every custom <SpecificTag name="react-js" overwriteTitle="React" showCount={true} /> component, and most of the page structure and information architecture.
                None of this was paid work &mdash; it's a passion project for a place I care about.
                I also couldn't have finished it alone. What started as a solo project grew as several friends and fellow residents pitched in - gathering information from the dormitory's many committees, writing and proofreading texts, and translating everything into English.
                By the end, there was a proper homepage team doing the finishing touches before launch, even while I was busy with my day job.
            </p>
        </section>

        <section>
            <h2>Retiring a Decade of WordPress</h2>
            <p>
                The old site had grown for years, patched together in WordPress by whoever had the time and will to touch it.
                It looked its age, was German-only despite a large share of international residents, and its structure had turned into a maze of dropdown menus that even long-term residents got lost in.
                It also had nothing to do with the dormitory's actual visual identity used everywhere else, from posters to event branding.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-homepage/homepage-old-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-homepage/homepage-old-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-homepage/homepage-old-32.png"),
                    alt : "The old HaDiKo website, a dated WordPress site",
                    title : "The old website: functional, but dated, German-only and disconnected from HaDiKo's actual visual identity."
                }
            ]} />
            <p>
                Before writing a line of code, I sat down with the association's board to rethink the site from scratch: what residents and applicants actually need to find, how the many self-administration committees should be represented, and how the whole thing should look and feel.
            </p>
        </section>

        <section>
            <h2>A Visual Identity Rooted in HaDiKo's Own CI</h2>
            <p>
                Rather than inventing a new look, I built the site around HaDiKo's own corporate identity: its green color palette, its logo and the sober, welcoming tone the association uses in its official communication.
                I had already built up much of this identity over the years through other projects such as the <Link to="/project/hadiko-newcomer-handbook">newcomer handbook</Link> or the <Link to="/project/hadiko-heimfest-2022">Heimfest designs</Link>.
                The result had to feel like an official, trustworthy resource &mdash; important for a site that's often a prospective resident's very first impression of the dormitory &mdash; while still being pleasant to actually use every day, and a little playful, since it's still something made by and for students.
            </p>
            <p>
                I also introduced a consistent navigation structure across the whole site: a persistent top bar for the main sections with some fancy collapsing animations and a contextual sidebar for the dozens of subpages underneath, so residents and applicants are never more than two clicks from what they're looking for.
            </p>
        </section>

        <section>
            <h2>Custom Components for a Lot of Recurring Content</h2>
            <p>
                HaDiKo's self-administration has a small mountain of documented committees, roles, rules and FAQs, and it kept coming back in slightly different shapes across the site.
                Rather than hand-writing similar-looking pages over and over, I built a set of reusable <SpecificTag name="react-js" overwriteTitle="React" showCount={true} /> components that content could just be dropped into.
            </p>
            <p>
                An expandable card component ended up doing most of the heavy lifting, used for explaining every committee and elected board as well as for the sprawling &ldquo;HaDiKo from A to Z&rdquo; glossary page:
            </p>
            <ImgGallery cols={2} images={[
                {
                    src : require("../../../media/projects/hadiko-homepage/homepage-committees-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-homepage/homepage-committees-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-homepage/homepage-committees-32.png"),
                    alt : "The committees of the self-administration page, using card components to explain each board",
                    title : "Each committee and board of the self-administration explained in the same reusable card component."
                },
                {
                    src : require("../../../media/projects/hadiko-homepage/homepage-dormitory-az-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-homepage/homepage-dormitory-az-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-homepage/homepage-dormitory-az-32.png"),
                    alt : "The HaDiKo from A to Z page, using collapsible sections for every topic",
                    title : "“HaDiKo from A to Z”: the same component, this time collapsed into an accordion to keep a long page manageable."
                }
            ]} />
            <p>
                For the application process, a linear, one-thing-happens-after-another kind of story, I built a vertical timeline component instead, complete with icons for every step from submitting documents to moving in:
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-homepage/homepage-moving-in-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-homepage/homepage-moving-in-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-homepage/homepage-moving-in-32.png"),
                    alt : "The application process page, using a vertical timeline component",
                    title : "A custom timeline component guides applicants through the admission process step by step."
                }
            ]} />
        </section>

        <section>
            <h2>Presenting Everyday Life in HaDiKo</h2>
            <p>
                A lot of the site isn't about rules and committees at all &mdash; it's about convincing someone that they'd actually like living here.
                Pages like &ldquo;Living on a floor&rdquo; combine a hero image, a short highlight list and a photo gallery to give prospective residents an honest look at shared kitchens, floor communities and the general atmosphere.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-homepage/homepage-living-on-a-floor-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-homepage/homepage-living-on-a-floor-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-homepage/homepage-living-on-a-floor-32.png"),
                    alt : "The living on a floor page, showing kitchens and shared spaces",
                    title : "Giving prospective residents an honest look at shared kitchens and floor life."
                }
            ]} />
            <p>
                Getting the tone and content right here relied heavily on the friends who helped me: gathering photos from residents, writing approachable copy instead of dry association-speak, and translating all of it into English for the sizeable share of international students applying every semester.
            </p>
        </section>

        <section>
            <h2>Responsive by Default</h2>
            <p>
                Plenty of applicants first encounter HaDiKo's website on their phone, often from abroad and at odd hours while comparing dormitories.
                Every component, from the navigation down to the accordions and timeline, was built mobile-first with the layout collapsing gracefully: multi-column pages fold into a single column, and the top navigation turns into a hamburger menu.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-homepage/homepage-responsive-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-homepage/homepage-responsive-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-homepage/homepage-responsive-32.png"),
                    alt : "The website displayed on a mobile phone",
                    title : "The same components, gracefully collapsing into a single-column mobile layout."
                }
            ]} />
        </section>

        <section>
            <h2>Summary</h2>
            <p>
                Seeing HaDiKo's committees, its floors and its whole application process finally represented properly online &mdash; and in English, too &mdash; is something I'm genuinely proud of.
                None of us got paid a cent for this: not me, not the friends who tracked down committee members for interviews, wrote texts or translated pages late at night.
                It's exactly the kind of self-administered, volunteer-driven effort HaDiKo is built on, just applied to a website instead of a kitchen roster.
            </p>
            <p>
                If you ever apply to <ExternalLink href="https://hadiko.de">HaDiKo</ExternalLink> yourself, there's a decent chance the first page you land on is one I built.
                I also reused parts of this visual identity when <Link to="/project/hadiko-heimfest-2022">designing marketing material for the Heimfest</Link> in the years that followed, which was a nice bit of continuity between projects.
            </p>
        </section>
    </>);
}
